import { useRef, useState } from 'react'
import {
  ALL_SIDES_UNMIXED,
  DEFAULT_OFFSET_STRINGS,
  NO_OFFSET_ERRORS,
  OFFSET_SIDES,
} from '../constants/wrap-offsets'
import { aggregateOffsetStrings } from '../helpers/offset-display'
import type { OffsetKey, OffsetStrings } from '../types/wrap-offset'

/** Display state of the four directional offset inputs (text, "Mixed" flag, validation error),
 *  plus the per-side focus and last-valid refs the sync/commit paths consult. */
export function useSideOffsetInputs() {
  const [offsetInputs, setOffsetInputs] = useState<OffsetStrings>(DEFAULT_OFFSET_STRINGS)
  const [offsetMixed, setOffsetMixed] = useState<Record<OffsetKey, boolean>>(ALL_SIDES_UNMIXED)
  const offsetInputFocused = useRef<Record<OffsetKey, boolean>>({ ...ALL_SIDES_UNMIXED })
  /** Empty string = valid. Non-empty holds the message to display (see `offsetValidationMessage`). */
  const [offsetErrorMessages, setOffsetErrorMessages] =
    useState<Record<OffsetKey, string>>(NO_OFFSET_ERRORS)
  /** Last known-valid string per side, restored on blur when the field is empty/invalid — matches
   *  the Kerning/Leading `kerningLastValidInputRef` pattern. */
  const offsetLastValidRef = useRef<Record<OffsetKey, string>>({ ...DEFAULT_OFFSET_STRINGS })

  /** No eligible target: every side back to the 6pt default, unmixed, no errors. */
  const reset = () => {
    setOffsetInputs(DEFAULT_OFFSET_STRINGS)
    setOffsetMixed(ALL_SIDES_UNMIXED)
    setOffsetErrorMessages(NO_OFFSET_ERRORS)
    offsetLastValidRef.current = { ...DEFAULT_OFFSET_STRINGS }
  }

  /** Shows the selection's aggregated per-side values (one `offsetStringsFromObj` per target). */
  const syncFromTargets = (perTarget: OffsetStrings[]) => {
    setOffsetInputs((prev) => {
      const { strings, mixed } = aggregateOffsetStrings(perTarget)

      // Don't clobber a side the user is actively typing in — matches the Kerning/Leading
      // input pattern elsewhere in this app.
      for (const { key } of OFFSET_SIDES) {
        if (offsetInputFocused.current[key]) {
          strings[key] = prev[key]
        } else {
          offsetLastValidRef.current[key] = strings[key]
        }
      }

      setOffsetMixed((prevMixed) => {
        const next = { ...mixed }

        for (const { key } of OFFSET_SIDES) {
          if (offsetInputFocused.current[key]) next[key] = prevMixed[key]
        }

        return next
      })

      return strings
    })
    setOffsetErrorMessages((prev) => {
      const next = { ...prev }

      for (const { key } of OFFSET_SIDES) {
        if (!offsetInputFocused.current[key]) next[key] = ''
      }

      return next
    })
  }

  /** Shows `value` on all four sides (the shared control just committed it). */
  const showAll = (value: number) => {
    setOffsetInputs((prev) => {
      const nextInputs = { ...prev }

      for (const { key } of OFFSET_SIDES) nextInputs[key] = String(value)

      return nextInputs
    })
    setOffsetMixed(ALL_SIDES_UNMIXED)
    setOffsetErrorMessages((prev) => {
      const nextErrors = { ...prev }

      for (const { key } of OFFSET_SIDES) nextErrors[key] = ''

      return nextErrors
    })

    for (const { key } of OFFSET_SIDES) {
      offsetLastValidRef.current[key] = String(value)
    }
  }

  /** Shows one side's just-committed value and records it as that side's last valid text. */
  const markCommitted = (key: OffsetKey, value: number) => {
    setOffsetInputs((prev) => ({ ...prev, [key]: String(value) }))
    offsetLastValidRef.current[key] = String(value)
  }

  /** Always show exactly what was typed — letters included — with its validation message. */
  const showTyped = (key: OffsetKey, raw: string, message: string) => {
    setOffsetInputs((prev) => ({ ...prev, [key]: raw }))
    setOffsetErrorMessages((prev) => ({ ...prev, [key]: message }))
  }

  /** Restores one side's last valid text and clears its error. */
  const revert = (key: OffsetKey) => {
    setOffsetInputs((prev) => ({
      ...prev,
      [key]: offsetLastValidRef.current[key],
    }))
    setOffsetErrorMessages((prev) => ({ ...prev, [key]: '' }))
  }

  const setFocused = (key: OffsetKey, focused: boolean) => {
    offsetInputFocused.current[key] = focused
  }

  return {
    offsetInputs,
    offsetMixed,
    offsetErrorMessages,
    reset,
    syncFromTargets,
    showAll,
    markCommitted,
    showTyped,
    revert,
    setFocused,
  }
}
